import { useCallback, useState, type FormEvent } from 'react'

export type FieldErrors = Record<string, string>

export type FormSubmitResult = {
    /** Chyby jednotlivých polí vrácené serverem. */
    fieldErrors?: FieldErrors
    /** Nastav na true, pokud odeslání selhalo z jiného důvodu. */
    failed?: boolean
}

export type UseFormStateOptions<TValues> = {
    initialValues: TValues
    onSubmit: (values: TValues) => Promise<FormSubmitResult | void>
}

/**
 * Minimální stav formuláře: hodnoty, chyby polí a příznak odesílání.
 * Chyby ze serveru se do polí dostanou přes výsledek `formSubmit`.
 */
export function useFormState<TValues extends Record<string, unknown>>({ initialValues, onSubmit }: UseFormStateOptions<TValues>) {
    const [values, setValues] = useState<TValues>(initialValues)
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})
    const [submitting, setSubmitting] = useState(false)

    const setValue = useCallback(<TKey extends keyof TValues>(name: TKey, value: TValues[TKey]) => {
        setValues((previous) => ({ ...previous, [name]: value }))
        setFieldErrors((previous) => {
            if (!(name in previous)) {
                return previous
            }
            const { [name as string]: _removed, ...rest } = previous
            return rest
        })
    }, [])

    const reset = useCallback(
        (next?: TValues) => {
            setValues(next ?? initialValues)
            setFieldErrors({})
        },
        [initialValues],
    )

    const handleSubmit = useCallback(
        async (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault()
            if (submitting) {
                return
            }

            setSubmitting(true)
            setFieldErrors({})
            try {
                const result = await onSubmit(values)
                if (result?.fieldErrors) {
                    setFieldErrors(result.fieldErrors)
                }
            } finally {
                setSubmitting(false)
            }
        },
        [onSubmit, submitting, values],
    )

    return { values, setValue, setValues, fieldErrors, setFieldErrors, submitting, handleSubmit, reset }
}
